(() => {
  let isRegistered = false;

  const createPredictiveSearch = (config = {}) => ({
    query: '',
    loading: false,
    resultsOpen: false,
    hasError: false,
    requestController: null,
    requestSequence: 0,
    cache: new Map(),
    minimumQueryLength: 2,
    endpoint: config.endpoint || `${window.Shopify?.routes?.root || '/'}search/suggest`,
    sectionId: config.sectionId || 'predictive-search',
    resourceTypes: config.resourceTypes || 'query,product,collection',

    init() {
      this.query = this.$refs.predictiveSearchInput?.value || '';
    },

    focusInput() {
      this.$refs.predictiveSearchInput?.focus();
    },

    handleFocus() {
      if (this.query.trim().length >= this.minimumQueryLength && this.$refs.results?.innerHTML.trim()) {
        this.resultsOpen = true;
      }
    },

    clearQuery() {
      this.query = '';
      this.cancelRequest();
      this.clearResults();
      this.$nextTick(() => this.focusInput());
    },

    clearResults() {
      this.loading = false;
      this.resultsOpen = false;
      this.hasError = false;

      if (this.$refs.results) {
        this.$refs.results.innerHTML = '';
      }
    },

    closeResults() {
      this.resultsOpen = false;
      this.hasError = false;
    },

    cancelRequest() {
      if (this.requestController) {
        this.requestController.abort();
        this.requestController = null;
      }
    },

    buildRequestUrl(searchTerm) {
      const url = new URL(this.endpoint, window.location.origin);

      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[type]', this.resourceTypes);
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'all');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', this.sectionId);

      return url;
    },

    renderResults(markup) {
      if (!this.$refs.results) {
        return;
      }

      this.$refs.results.innerHTML = markup;
      this.resultsOpen = true;
      this.hasError = false;
    },

    rememberResult(key, markup) {
      if (this.cache.size >= 20) {
        const oldestKey = this.cache.keys().next().value;
        this.cache.delete(oldestKey);
      }

      this.cache.set(key, markup);
    },

    async loadSuggestions() {
      const searchTerm = this.query.trim();

      if (searchTerm.length < this.minimumQueryLength) {
        this.cancelRequest();
        this.clearResults();
        return;
      }

      const cacheKey = searchTerm.toLocaleLowerCase();
      const cachedMarkup = this.cache.get(cacheKey);

      if (cachedMarkup !== undefined) {
        this.loading = false;
        this.renderResults(cachedMarkup);
        return;
      }

      this.cancelRequest();
      this.requestController = new AbortController();
      const sequence = ++this.requestSequence;
      this.loading = true;
      this.resultsOpen = false;
      this.hasError = false;

      try {
        const response = await fetch(this.buildRequestUrl(searchTerm).toString(), {
          signal: this.requestController.signal,
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error(`Predictive search request failed with status ${response.status}`);
        }

        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const section = responseDocument.querySelector(`#shopify-section-${this.sectionId}`);

        if (!section) {
          throw new Error('Predictive search response did not include the requested section');
        }

        if (sequence !== this.requestSequence || searchTerm !== this.query.trim()) {
          return;
        }

        const markup = section.innerHTML;
        this.rememberResult(cacheKey, markup);
        this.renderResults(markup);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        if (sequence === this.requestSequence) {
          this.resultsOpen = false;
          this.hasError = true;
        }
      } finally {
        if (sequence === this.requestSequence) {
          this.loading = false;
          this.requestController = null;
        }
      }
    },

    resultLinks() {
      if (!this.$refs.results) {
        return [];
      }

      return Array.from(this.$refs.results.querySelectorAll('[data-predictive-search-result]'));
    },

    focusBoundaryResult(boundary) {
      if (!this.resultsOpen) {
        this.loadSuggestions();
        return;
      }

      const links = this.resultLinks();
      const target = boundary === 'last' ? links.at(-1) : links[0];
      target?.focus();
    },

    focusAdjacentResult(event, direction) {
      const links = this.resultLinks();
      const currentIndex = links.indexOf(event.target.closest('[data-predictive-search-result]'));

      if (currentIndex < 0) {
        return;
      }

      const nextIndex = currentIndex + direction;

      if (nextIndex < 0 || nextIndex >= links.length) {
        this.focusInput();
        return;
      }

      links[nextIndex].focus();
    },
  });

  const registerPredictiveSearch = () => {
    if (!window.Alpine || isRegistered) {
      return;
    }

    isRegistered = true;
    window.Alpine.data('predictiveSearch', createPredictiveSearch);
  };

  window.JustFishyPredictiveSearch = {
    createPredictiveSearch,
  };

  document.addEventListener('alpine:init', registerPredictiveSearch);
  registerPredictiveSearch();
})();
